import { useState, useEffect } from 'react';
import { Course } from '@/types/data';

interface UseCoursesReturn {
  loading: boolean;
  error: string | null;
  getCourseBySlug: (slug: string) => Promise<Course | undefined>;
  getCourses: (limit?: number, category_slug?: string) => Promise<Course[]>;
  getCourseByTeacherSlug: (slug: string) => Promise<Course[] | undefined>;
  searchCourses: (
    page: number,
    limit: number,
    category: string | null,
    keySearch?: string
  ) => Promise<{ courses: Course[]; total: number }>;
}

export const useCourses = (): UseCoursesReturn => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load all courses once when the hook is initialized

  const getCourses = async (limit?: number, category_slug?: string): Promise<Course[]> => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query parameters
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());
      if (category_slug) params.append('category_slug', category_slug);
      
      const url = `/api/courses${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url);
      console.log(response);
      
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.success ? data.data : [];
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch courses');
    } finally {
      setLoading(false);
    }
    return [];
  };

  const getCourseBySlug = async (slug: string): Promise<Course | undefined> => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/courses/${slug}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          return undefined;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.success ? data.data : undefined;
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch course');
    } finally {
      setLoading(false);
    }
    return undefined;
  };

  const getCourseByTeacherSlug = async (slug: string): Promise<Course[] | undefined> => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/courses/teacher/${slug}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.success ? data.data : [];
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch course');
    } finally {
      setLoading(false);
    }
    return undefined;
  };

  const searchCourses = async (
    page: number,
    limit: number,
    category: string | null,
    keySearch?: string
  ): Promise<{ courses: Course[]; total: number }> => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (category) params.append('category', category);
      if (keySearch) params.append('key', keySearch);
      
      const url = `/api/courses/search?${params.toString()}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.success ? data.data : { courses: [], total: 0 };
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to search courses');
      return { courses: [], total: 0 };
    } finally {
      setLoading(false);
    }
  };

  return { loading, error, getCourseBySlug, getCourses, getCourseByTeacherSlug, searchCourses };
};
